import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import {
    DataTable,
    type TableColumn,
    ActivateIcon,
    DeactivateIcon,
    EditIcon,
    TrashIcon,
} from '@/src/ui';
import type { ExamDTO } from '../models/examDTOs';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

interface ExamsTableProps {
    data: ExamDTO[];
    showPublishModal: boolean;
    setShowPublishModal: (val: boolean) => void;
    showDeleteModal: boolean;
    setShowDeleteModal: (val: boolean) => void;
    setSelectedExam: (val: ExamDTO | null) => void;
    searchString: string;
}

export default function ExamsTable(props: ExamsTableProps) {
    const {
        data,
        showPublishModal,
        showDeleteModal,
        setShowPublishModal,
        setShowDeleteModal,
        setSelectedExam,
        searchString,
    } = props;

    const filteredData = data.filter((exam) => {
        const search = searchString.trim().toLowerCase();
        if (!search) return true;
        return exam.title.trim().toLowerCase().includes(search);
    });

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'id',
                header: 'ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'title',
                header: 'Title',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examTypeMaster',
                header: 'Assessment Type',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examGrades',
                header: 'Exam Grades',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamDTO) => {
                    return (
                        row.examGrades.map((grade) => grade.gradeLevelName).join(', ') ||
                        '<No Grades>'
                    );
                },
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamDTO) => {
                    return (
                        <>
                            <Tooltip title="Edit" arrow>
                                <Link
                                    to={`/examsAdmin/createEditExam?viewMode=edit&resourceId=${row.id}&examTypeMasterId=${row.examTypeMasterId}`}
                                    style={{
                                        marginRight: 8,
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.6em 1.2em',
                                        textDecoration: 'none',
                                    }}
                                >
                                    <span role="img" aria-label="edit">
                                        <EditIcon />
                                    </span>
                                </Link>
                            </Tooltip>
                            <Tooltip title={row.isPublished ? 'Published' : 'Not Published'} arrow>
                                <button
                                    style={{
                                        marginRight: 8,
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        if (!showPublishModal) {
                                            setSelectedExam(row);
                                            setShowPublishModal(true);
                                        }
                                    }}
                                >
                                    <span role="img" aria-label="edit">
                                        {row.isPublished ? (
                                            <ActivateIcon color="green" />
                                        ) : (
                                            <DeactivateIcon color="red" />
                                        )}
                                    </span>
                                </button>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        if (!showDeleteModal) {
                                            setSelectedExam(row);
                                            setShowDeleteModal(true);
                                        }
                                    }}
                                >
                                    <span role="img" aria-label="delete">
                                        <TrashIcon />
                                    </span>
                                </button>
                            </Tooltip>
                        </>
                    );
                },
            },
        ];
    }, [
        showDeleteModal,
        setShowDeleteModal,
        setSelectedExam,
        showPublishModal,
        setShowPublishModal,
    ]);

    return (
        <DataTable
            data={filteredData}
            columns={columns}
            tableName="Users"
            alternatingRowColor
            defaultSortColumn="title"
            defaultSortDirection="asc"
        />
    );
}
